import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/frontend/components/ui/collapsible";
import { cn } from "@/frontend/lib/utils";
import { DESTINATION_TYPE } from "@/server/types/export";
import {
  computeExportProgress,
  formatTimeAgo,
} from "@/frontend/utils/dashboard/settings/export";
import { getDestinationLogo, getDestTypeLabel } from "@/frontend/utils/dashboard/settings/activity";
import { TRIGGER_LABELS } from "@/frontend/types/dashboard/settings/activity";

export function RunningBanner({ run }: { run: any }) {
  const isQueued = run.status === "queued";
  const isDelta = run.delta_from != null;
  const destTypeLabel = getDestTypeLabel(run);
  const { total, done, pct } = computeExportProgress(run);
  const isFhir = run.destination_type === DESTINATION_TYPE.FHIR_SERVER;

  return (
    <Collapsible className={cn("rounded-lg border overflow-hidden", isQueued ? "border-gray-400/20 bg-gray-50/50 dark:bg-gray-950/10" : "border-blue-500/20 bg-blue-50/50 dark:bg-blue-950/10")}>
      <CollapsibleTrigger className="flex flex-col w-full px-4 py-3 cursor-pointer text-left group">
        {/* Row 1: destination + progress % */}
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2.5">
            <img src={getDestinationLogo(run)} alt={destTypeLabel} className="w-5 h-5 object-contain shrink-0" />
            <span className="text-sm font-medium">{run.destination_name}</span>
            <span className="text-xs text-muted-foreground">{destTypeLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {isQueued ? "Queued" : `${pct}%`}
            </span>
            <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </div>
        </div>
        {/* Row 2: progress bar */}
        {!isQueued && total > 0 && (
          <div className="flex items-center gap-3 mt-2 w-full">
            <div className="h-1.5 bg-blue-200 dark:bg-blue-800/30 rounded-full flex-1 overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-4 pb-4">
          <div className="grid grid-cols-[80px_1fr_80px_1fr] gap-x-3 gap-y-1.5 text-xs pt-3 border-t border-blue-200 dark:border-blue-800/30">
            <span className="text-muted-foreground">Status</span>
            <span className={cn("font-medium", isQueued ? "text-gray-600 dark:text-gray-400" : "text-blue-600 dark:text-blue-400")}>{isQueued ? "Queued" : "Running"}</span>
            <span className="text-muted-foreground">Trigger</span>
            <span>{TRIGGER_LABELS[run.trigger] ?? run.trigger}</span>
            <span className="text-muted-foreground">Type</span>
            <span className={cn("font-medium", isDelta ? "text-blue-600 dark:text-blue-400" : "text-purple-600 dark:text-purple-400")}>
              {isDelta ? "Delta" : "Full"}
            </span>
            <span className="text-muted-foreground">Resources</span>
            <span>{isFhir ? `${done} / ${total}` : (run.items_exported ?? 0)}</span>
            <span className="text-muted-foreground">Started</span>
            <span>{formatTimeAgo(run.started_at)}</span>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
